import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Services } from '@/services/services';

import './Main.css'
import { Translates } from '@/services/repository/i18n';
import { MainPageLayout } from '@/components/layout/MainLayout';
import { MachinePath } from '../../Common';
import { Machine } from '@/services/model/machine';
import { CurrentSession } from '@/services/repository/session';
import { MachineFailureCategory } from '@/services/repository/machines';
import { Failure, LoadFailure, SaveFailure } from './Model';
import { BackButton } from '@/components/layout/NavBar';
import { SpeechRecognition, SpeechRecognitionEvent } from '@/@types/global';

export const AddFailureDescription = ({ services, session }: { services: Services, session?: CurrentSession }) => {
  const navigate = useNavigate()
  const machineId = useParams().machineid
  const [translates, setTranslates] = useState<Translates>()
  const [machine, setMachine] = useState<Machine>()
  const [failure, setFailure] = useState<Failure>()
  const [categories, setCategories] = useState<MachineFailureCategory[]>(services.machines.listFailureCategories())
  // const [isListening, setIsListening] = useState(false);
  // const [timeElapsed, setTimeElapsed] = useState(0);
  const recognitionRef = useRef<{
    recognition: SpeechRecognition | null
    baseText: string
    isListening: boolean
  }>({
    recognition: null,
    baseText: '',
    isListening: false
  });

  useEffect(() => {
    const unsafeWindow = window as any
    const SpeechRecognition = unsafeWindow.SpeechRecognition || unsafeWindow.webkitSpeechRecognition
    const lang = session?.user.lang || services.i18n.defaultLang() || 'pl'

    if (!SpeechRecognition) {
      console.log('Speech recognition not supported in this browser.');
      return;
    }
    const recognition = new SpeechRecognition()
    recognitionRef.current.recognition = recognition
    recognition.continuous = true
    // recognition.interimResults = true
    recognition.lang = `${lang.toLowerCase()}-${lang.toUpperCase()}`

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = ''
      let intTranscript = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        let result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          intTranscript += result[0].transcript
        }
      }

      setFailure((prev) => {
        if (!prev) return prev;
        recognitionRef.current.baseText = (recognitionRef.current.baseText + " " + finalTranscript.trim()).trim()
        return { ...prev, description: (recognitionRef.current.baseText + " " + intTranscript).trim() };
      })
    }

    recognition.onerror = (event: any) => {
      console.error('Error occurred in recognition:', event.error);
    };

    recognition.onend = () => {
      if (recognitionRef.current.isListening) {
        recognitionRef.current?.recognition?.start()
      }
    }

    return () => {
      recognitionRef.current.isListening = false
      recognitionRef.current?.recognition?.stop();
    };
  }, [session?.user.lang]);

  const toggleListening = () => {
    if (!recognitionRef.current.recognition || failure === undefined) return;
    recognitionRef.current.baseText = failure?.description || ''
    if (recognitionRef.current.isListening) {
      recognitionRef.current.recognition.stop()
      recognitionRef.current.isListening = false
    } else {
      recognitionRef.current.recognition.start()
      recognitionRef.current.isListening = true
    }
    setFailure({
      ...failure
    })
  };

  useEffect(() => {
    if (session) {
      setTranslates(session.translates)
      return
    }
    services.i18n.get('main').then(setTranslates)
  }, [session])

  useEffect(() => {
    fetch(`/api/communal/machines/byid/${machineId}`).then((response) => {
      return response.json()
    }).then((data) => {
      setMachine(data)
    })
  }, [machineId])

  useEffect(() => {
    if (machineId === undefined) {
      return
    }
    setFailure(LoadFailure(machineId))
  }, [machineId])

  if (translates === undefined || machine === undefined || translates === undefined || failure === undefined || machineId === undefined) {
    return <div className='spinner'></div>
  }

  return <MainPageLayout pageName='add-failure' services={services} translates={translates} session={session} path={MachinePath({ session, translates, machine })}
    navbarLeft={<BackButton services={services} translates={translates} replaceUrl={`/communal/machine/${machineId}/failures/add/page2`} />}>

    <h2 className='page-title'>{translates.for(`failures.description.title`)}</h2>

    <div className='description-box'>
      <textarea
        value={failure.description}
        onChange={(e) => setFailure((prev) => {
          if (!prev) return prev;
          return { ...prev, description: e.target.value }; // Ensure the state update is immutable
        })}
        placeholder={translates.for(`failures.description.placeholder`)}
        readOnly={recognitionRef.current.isListening}
      />
    </div>

    <div className={recognitionRef.current.isListening ? 'record-btn pulse' : 'record-btn'} onClick={toggleListening}>
      {recognitionRef.current.isListening ? translates.for(`failures.description.stop_recording`) : translates.for(`failures.description.record`)}
      <img src={`/public/img/icons/microphone.svg`} style={{ opacity: recognitionRef.current.isListening ? 0.5 : 1 }} />
    </div>

    <div className='approve-btn' onClick={() => {
      SaveFailure(machineId, failure)
      navigate(`/communal/machine/${machineId}/failures/add/page2`, { replace: true })
    }}>
      {translates.for(`failures.form.approve`)}
    </div>


  </MainPageLayout>
}
